/**
 * VirtualPhotoGrid - Virtual scrolling for photo grid (Photos page)
 * 
 * Only renders photos that are visible in the viewport plus a buffer (overscan).
 * Dramatically improves performance with large photo collections.
 * 
 * Now supports lazy loading: Preloads full photo data for visible photos + 30% buffer,
 * so clicking on photos to edit them is instant.
 */

import { PhotoStatusOverlay } from './photo_status_overlay.js';

export class VirtualPhotoGrid {
  constructor(container, options = {}) {
    this.container = container;
    this.scrollContainer = options.scrollContainer || container.parentElement;
    
    // Grid configuration
    this.itemHeight = options.itemHeight || 128; // h-28 (112px) + padding + ring
    this.itemGap = options.itemGap || 16; // gap-4 (16px)
    this.overscanRows = options.overscanRows || 3; // Render 3 extra rows above/below
    this.preloadOverscanRows = options.preloadOverscanRows || 10; // Preload 10 extra rows for fast scrolling
    
    // Lazy loading
    this.viewportPhotoLoader = options.viewportPhotoLoader || null;
    this.photoService = options.photoService || null;
    this.lastLoadedRange = { start: -1, end: -1 }; // Track last loaded range to avoid duplicate requests
    this.preloadDebounceTimer = null; // Debounce preloading during rapid scrolling
    
    // State management
    this.folderStateManager = options.folderStateManager || null;
    this.editorState = options.editorState || null;
    this.currentFolderId = null;
    
    // Data
    this.photos = []; // Basic photo metadata (id, filename, thumb, processing_status)
    this.selectedPhotos = new Set();
    this.activePhoto = null;
    
    // Viewport state
    this.columnsCount = 1;
    this.visibleRange = { start: 0, end: 0 };
    this.renderedItems = new Map(); // photoId -> element
    
    // Performance
    this.renderScheduled = false;
    this.resizeObserver = null;
    this.scrollDebounceTimer = null;
    this.scrollDebounceDelay = 150; // ms delay before loading images during scroll
    
    // Progressive image loading
    this.lastScrollTime = 0;
    this.scrollVelocity = 0; // Events per window
    this.scrollEventCount = 0;
    this.scrollVelocityTimer = null;
    this.isScrollingCrazyFast = false;
    this.imageLoadTimer = null;
    this.crazyFastScrollThreshold = 20; // ms - check velocity within this window
    this.crazyFastEventThreshold = 5; // Need 5+ events in 20ms to be "crazy fast"
    
    this.setupContainer();
    this.setupScrollListener();
    this.setupResizeObserver();
    this.setupEventListeners();
  }
  
  /**
   * Setup event listeners for reactive updates
   */
  setupEventListeners() {
    if (this.folderStateManager) {
      // Listen to folder state manager for new folder states
      this.folderStateManager.addEventListener('folderStateCreated', (e) => {
        this.attachFolderStateListeners(e.detail.folderState);
      });
      
      // Attach to existing folder states
      this.folderStateManager.getAllFolderStates().forEach(folderState => {
        this.attachFolderStateListeners(folderState);
      });
    }
    
    if (this.editorState) {
      // Listen for selection changes
      this.editorState.addEventListener('selectedPhotoIds', (e) => {
        // Update selection state and re-render visible items
        this.selectedPhotos = e.detail.value || new Set();
        this.updateVisibleSelectionState();
      });
      
      // Listen for active photo changes
      this.editorState.addEventListener('activePhotoId', (e) => {
        const state = this.editorState.get();
        this.activePhoto = state.activePhotoId ? { id: state.activePhotoId } : null;
        this.updateVisibleSelectionState();
      });
    }
  }
  
  /**
   * Update selection state for all currently rendered items
   */
  updateVisibleSelectionState() {
    this.renderedItems.forEach((element, photoId) => {
      const photo = this.photos.find(p => p.id === photoId);
      const index = this.photos.findIndex(p => p.id === photoId);
      if (photo && index !== -1) {
        this.updatePhotoElement(element, photo, index);
      }
    });
  }
  
  /**
   * Attach listeners to a specific FolderState
   */
  attachFolderStateListeners(folderState) {
    // When a photo is updated, efficiently update just that photo's element
    folderState.addEventListener('photoUpdated', (e) => {
      // Only update if this is the currently displayed folder
      if (this.currentFolderId !== folderState.getId()) {
        return;
      }
      
      const { photo } = e.detail;
      const index = this.photos.findIndex(p => p.id === photo.id);
      
      if (index !== -1) {
        // Update photo data in our array
        this.photos[index] = { ...this.photos[index], ...photo };
        
        // If this photo is currently rendered, update its element
        const element = this.renderedItems.get(photo.id);
        if (element) {
          this.updatePhotoElement(element, this.photos[index], index);
          
          // Force immediate visibility check - ensure the update is painted
          // requestAnimationFrame ensures the DOM update is processed before next frame
          requestAnimationFrame(() => {
            const img = element.querySelector('img');
          });
        }
      }
    });
  }
  
  setupContainer() {
    // Ensure scroll container has proper styling
    if (this.scrollContainer) {
      // Don't override existing scroll behavior, just ensure positioning
      if (!this.scrollContainer.style.position || this.scrollContainer.style.position === 'static') {
        this.scrollContainer.style.position = 'relative';
      }
    }
    
    // Grid container should be positioned for absolute children
    // Remove CSS grid classes and use relative positioning instead
    this.container.style.position = 'relative';
    this.container.style.display = 'block'; // Override grid display
    this.container.style.minHeight = '0'; // Will be set dynamically
    this.container.classList.remove('grid'); // Remove Tailwind grid class
  }
  
  setupScrollListener() {
    if (!this.scrollContainer) return;
    
    this.scrollContainer.addEventListener('scroll', () => {
      this.trackScrollVelocity();
      this.scheduleRender();
    }, { passive: true });
  }
  
  /**
   * Track scroll velocity to determine if we're scrolling crazy fast
   */
  trackScrollVelocity() {
    const now = performance.now();
    const timeSinceLastScroll = now - this.lastScrollTime;
    
    // Only count as "crazy fast" if events are extremely rapid (< 5ms apart)
    // This catches aggressive trackpad flicks or rapid mouse wheel spins
    if (timeSinceLastScroll < 5) {
      this.scrollEventCount++;
      // Need sustained burst of very rapid events
      this.isScrollingCrazyFast = this.scrollEventCount >= this.crazyFastEventThreshold;
    } else {
      // Normal scroll timing - reset counter
      this.scrollEventCount = 0;
      this.isScrollingCrazyFast = false;
    }
    
    this.lastScrollTime = now;
    
    // Reset velocity counter after scrolling stops
    if (this.scrollVelocityTimer) {
      clearTimeout(this.scrollVelocityTimer);
    }
    this.scrollVelocityTimer = setTimeout(() => {
      this.scrollEventCount = 0;
      this.isScrollingCrazyFast = false;
      // When scrolling stops, load images for visible items
      this.scheduleImageLoad();
    }, 50);
  }
  
  setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.updateLayout();
      this.scheduleRender(true); // Immediate render for resize
    });
    
    this.resizeObserver.observe(this.container);
  }
  
  /**
   * Update with new photo data
   */
  update(photos, selectedPhotos, activePhoto, folderId = null) {
    this.photos = photos || [];
    this.selectedPhotos = selectedPhotos || new Set();
    this.activePhoto = activePhoto;
    this.currentFolderId = folderId;
    
    // Force update to re-render existing items with new selection state
    this.forceUpdate = true;
    
    this.updateLayout();
    this.scheduleRender(true); // Immediate render for folder changes
  }
  
  /**
   * Calculate grid layout (columns, total height)
   */
  updateLayout() {
    // Calculate columns based on container width
    // Match the CSS: grid-template-columns: repeat(auto-fill, minmax(150px, 1fr))
    const containerWidth = this.container.offsetWidth;
    const minItemWidth = 150; // Match CSS minmax(150px, 1fr)
    const gap = this.itemGap;
    
    // Calculate how many columns fit: (width + gap) * n - gap <= containerWidth
    this.columnsCount = Math.max(1, Math.floor((containerWidth + gap) / (minItemWidth + gap)));
    
    // Calculate total height
    const totalRows = Math.ceil(this.photos.length / this.columnsCount);
    const totalHeight = totalRows > 0 ? totalRows * (this.itemHeight + this.itemGap) - this.itemGap : 0;
    
    this.container.style.minHeight = `${totalHeight}px`;
  }
  
  /**
   * Calculate which photos should be visible
   */
  calculateVisibleRange() {
    if (!this.scrollContainer || this.photos.length === 0) {
      return { start: 0, end: 0 };
    }
    
    const scrollTop = this.scrollContainer.scrollTop;
    const viewportHeight = this.scrollContainer.clientHeight;
    const rowHeight = this.itemHeight + this.itemGap;
    
    // Calculate visible rows
    const firstVisibleRow = Math.floor(scrollTop / rowHeight);
    const lastVisibleRow = Math.ceil((scrollTop + viewportHeight) / rowHeight);
    
    // Add overscan
    const startRow = Math.max(0, firstVisibleRow - this.overscanRows);
    const endRow = Math.min(
      Math.ceil(this.photos.length / this.columnsCount),
      lastVisibleRow + this.overscanRows
    );
    
    // Convert to item indices
    const start = startRow * this.columnsCount;
    const end = Math.min(this.photos.length, endRow * this.columnsCount);
    
    return { start, end };
  }
  
  /**
   * Calculate extended range for preloading images
   */
  calculatePreloadRange() {
    if (!this.scrollContainer || this.photos.length === 0) {
      return { start: 0, end: 0 };
    }
    
    const scrollTop = this.scrollContainer.scrollTop;
    const viewportHeight = this.scrollContainer.clientHeight;
    const rowHeight = this.itemHeight + this.itemGap;
    
    // Calculate visible rows
    const firstVisibleRow = Math.floor(scrollTop / rowHeight);
    const lastVisibleRow = Math.ceil((scrollTop + viewportHeight) / rowHeight);
    
    // Add larger preload overscan
    const startRow = Math.max(0, firstVisibleRow - this.preloadOverscanRows);
    const endRow = Math.min(
      Math.ceil(this.photos.length / this.columnsCount),
      lastVisibleRow + this.preloadOverscanRows
    );
    
    // Convert to item indices
    const start = startRow * this.columnsCount;
    const end = Math.min(this.photos.length, endRow * this.columnsCount);
    
    return { start, end };
  }
  
  /**
   * Schedule a render - now renders DOM immediately, images loaded progressively
   * @param {boolean} immediate - If true, skip debounce and render immediately
   */
  scheduleRender(immediate = false) {
    // Always render DOM structure immediately for responsive feel
    if (this.renderScheduled) return;
    
    this.renderScheduled = true;
    requestAnimationFrame(() => {
      this.renderScheduled = false;
      this.render();
    });
  }
  
  /**
   * Schedule progressive image loading
   */
  scheduleImageLoad() {
    if (this.imageLoadTimer) {
      clearTimeout(this.imageLoadTimer);
    }
    
    // Small delay to ensure scrolling has truly stopped
    this.imageLoadTimer = setTimeout(() => {
      this.loadVisibleImages();
    }, 50);
  }
  
  /**
   * Render visible items
   */
  render() {
    const newRange = this.calculateVisibleRange();
    
    // Skip if range hasn't changed AND we're not forcing an update
    if (newRange.start === this.visibleRange.start && 
        newRange.end === this.visibleRange.end &&
        this.renderedItems.size > 0 &&
        !this.forceUpdate) {
      return;
    }
    
    // Clear force update flag
    this.forceUpdate = false;
    
    this.visibleRange = newRange;
    
    // NOTE: We don't preload full photo data during grid scrolling!
    // The metadata already has thumb_256_url which is all we need to display thumbnails.
    // Preloading full photo data (with embeddings) takes 5-10 seconds and blocks scrolling.
    // Full data is only loaded on-demand when user clicks to edit a photo.
    
    // Get currently rendered photo IDs
    const currentIds = new Set(this.renderedItems.keys());
    const newIds = new Set(
      this.photos.slice(newRange.start, newRange.end).map(p => p.id)
    );
    
    // Remove items that are no longer visible
    currentIds.forEach(id => {
      if (!newIds.has(id)) {
        const element = this.renderedItems.get(id);
        if (element && element.parentElement) {
          element.remove();
        }
        this.renderedItems.delete(id);
      }
    });
    
    // Determine if we should load images immediately
    // Load images UNLESS scrolling is crazy fast
    const shouldLoadImages = !this.isScrollingCrazyFast;
    
    // Add/update visible items
    let created = 0;
    for (let i = newRange.start; i < newRange.end; i++) {
      const photo = this.photos[i];
      if (!photo) continue;
      
      let element = this.renderedItems.get(photo.id);
      
      if (!element) {
        // Create new element
        element = this.createPhotoElement(photo, i, shouldLoadImages);
        this.container.appendChild(element);
        this.renderedItems.set(photo.id, element);
        created++;
      } else {
        // Update existing element (position and state)
        this.updatePhotoElement(element, photo, i, shouldLoadImages);
      }
    }
    
    // If we loaded images (not scrolling crazy fast), also preload extended range
    if (shouldLoadImages) {
      this.preloadExtendedRange();
    }
  }
  
  /**
   * Preload images in extended range beyond visible viewport
   */
  preloadExtendedRange() {
    const preloadRange = this.calculatePreloadRange();
    
    // Preload images before visible range
    for (let i = preloadRange.start; i < this.visibleRange.start; i++) {
      this.preloadImageForIndex(i);
    }
    // Preload images after visible range
    for (let i = this.visibleRange.end; i < preloadRange.end; i++) {
      this.preloadImageForIndex(i);
    }
  }
  
  /**
   * Load images for currently visible items progressively
   * Also preloads images in extended range for better fast-scroll performance
   */
  loadVisibleImages() {
    const preloadRange = this.calculatePreloadRange();
    
    // Phase 1: Load visible images first (prioritize what user sees)
    for (let i = this.visibleRange.start; i < this.visibleRange.end; i++) {
      this.loadImageForIndex(i);
    }
    
    // Phase 2: Preload images in extended range (for fast scrolling)
    // Load before visible range
    for (let i = preloadRange.start; i < this.visibleRange.start; i++) {
      this.preloadImageForIndex(i);
    }
    // Load after visible range
    for (let i = this.visibleRange.end; i < preloadRange.end; i++) {
      this.preloadImageForIndex(i);
    }
  }
  
  /**
   * Load image for a specific index (creates img element in DOM)
   */
  loadImageForIndex(i) {
    const photo = this.photos[i];
    if (!photo) return;
    
    const element = this.renderedItems.get(photo.id);
    if (!element) return;
    
    const imgContainer = element.querySelector('.bg-gray-800');
    if (!imgContainer) return;
    
    let img = imgContainer.querySelector('img');
    const thumbUrl = photo.thumb_256_url || photo.thumb_1024_url;
    
    if (!img && thumbUrl) {
      // Create img element if it doesn't exist yet
      img = document.createElement('img');
      img.src = thumbUrl;
      img.alt = photo.original_filename;
      img.className = 'max-w-full max-h-full object-contain';
      img.decoding = 'async';
      img.loading = 'eager';
      imgContainer.appendChild(img);
    } else if (img && !img.src && thumbUrl) {
      // Set src if img exists but has no src
      img.src = thumbUrl;
    }
  }
  
  /**
   * Preload image for index (triggers browser cache without DOM element)
   */
  preloadImageForIndex(i) {
    const photo = this.photos[i];
    if (!photo) return;
    
    const thumbUrl = photo.thumb_256_url || photo.thumb_1024_url;
    if (!thumbUrl) return;
    
    // Check if already preloaded/loaded
    const element = this.renderedItems.get(photo.id);
    if (element) {
      // If element exists in DOM, load it normally
      this.loadImageForIndex(i);
      return;
    }
    
    // Preload into browser cache using Image object
    // This doesn't add to DOM but triggers the browser to fetch and cache
    const img = new Image();
    img.src = thumbUrl;
    // No need to keep reference - browser will cache it
  }
  
  /**
   * Schedule preloading with debounce to avoid API spam during rapid scrolling
   */
  schedulePreload(startIndex, endIndex) {
    // Clear existing timer
    if (this.preloadDebounceTimer) {
      clearTimeout(this.preloadDebounceTimer);
    }
    
    // Schedule new preload after scrolling settles (300ms)
    this.preloadDebounceTimer = setTimeout(() => {
      this.preloadPhotosInRange(startIndex, endIndex);
    }, 300);
  }
  
  /**
   * Preload full photo data for visible range + buffer
   * This makes clicking on photos instant (data already loaded)
   */
  async preloadPhotosInRange(startIndex, endIndex) {
    if (!this.viewportPhotoLoader || this.photos.length === 0) {
      return;
    }
    
    // Skip if we've already loaded this exact range
    if (startIndex === this.lastLoadedRange.start && endIndex === this.lastLoadedRange.end) {
      return;
    }
    
    this.lastLoadedRange = { start: startIndex, end: endIndex };
    
    try {
      // Calculate which photos need loading (visible + 30% buffer)
      const photoIdsToLoad = this.viewportPhotoLoader.calculatePhotosToLoad(
        this.photos,
        startIndex,
        endIndex,
        this.activePhoto?.id
      );
      
      if (photoIdsToLoad.length > 0) {
        await this.viewportPhotoLoader.loadPhotos(photoIdsToLoad);
      }
    } catch (error) {
      console.error('Error preloading photos:', error);
    }
  }
  
  /**
   * Create a photo grid item element
   * @param {Object} photo - Photo data
   * @param {number} index - Index in photos array
   * @param {boolean} shouldLoadImages - Whether to set img.src immediately
   */
  createPhotoElement(photo, index, shouldLoadImages = true) {
    const thumbUrl = photo.thumb_256_url || photo.thumb_1024_url;
    
    // Always create the full photo element structure, even without a URL
    // This ensures the gray placeholder is shown and can be updated later
    
    const isSelected = this.selectedPhotos.has(photo.id);
    const isActive = this.activePhoto?.id === photo.id;
    
    // Calculate position
    const row = Math.floor(index / this.columnsCount);
    const col = index % this.columnsCount;
    const top = row * (this.itemHeight + this.itemGap);
    
    // Calculate width and left position properly accounting for gaps
    // Each item: (100% - total gaps) / columns
    // Left position: col * (item width + gap)
    const totalGapsWidth = this.itemGap * (this.columnsCount - 1);
    const itemWidth = `calc((100% - ${totalGapsWidth}px) / ${this.columnsCount})`;
    const left = `calc(${col} * ((100% - ${totalGapsWidth}px) / ${this.columnsCount} + ${this.itemGap}px))`;
    
    // Outer wrapper with consistent padding to accommodate rings
    const wrapper = document.createElement('div');
    wrapper.className = 'p-1 absolute';
    wrapper.style.top = `${top}px`;
    wrapper.style.left = left;
    wrapper.style.width = itemWidth;
    wrapper.style.height = `${this.itemHeight}px`;
    wrapper.dataset.photoId = photo.id;
    wrapper.dataset.index = index;
    
    const div = document.createElement('div');
    // Use ring-4 styling to match preview carousel (doesn't affect layout)
    if (isActive) {
      div.className = 'relative cursor-pointer rounded ring-4 ring-white shadow-xl';
    } else if (isSelected) {
      div.className = 'relative cursor-pointer rounded ring-4 ring-blue-400 shadow-md';
    } else {
      div.className = 'relative cursor-pointer rounded hover:ring-2 hover:ring-gray-500';
    }
    
    // Container to maintain aspect ratio (h-28 = 112px to match original)
    const imgContainer = document.createElement('div');
    imgContainer.className = 'w-full h-28 flex items-center justify-center bg-gray-800 rounded';
    
    // Only create img element if we're loading images AND we have a URL
    // This prevents the broken image icon and null URL requests
    if (shouldLoadImages && thumbUrl) {
      const img = document.createElement('img');
      img.src = thumbUrl;
      img.alt = photo.original_filename;
      img.className = 'max-w-full max-h-full object-contain';
      img.decoding = 'async'; // Decode images off main thread
      img.loading = 'eager'; // Load immediately since virtual scrolling handles lazy loading
      imgContainer.appendChild(img);
    }
    // If no URL, just show the gray box (bg-gray-800) placeholder
    
    div.appendChild(imgContainer);
    
    // Add processing status overlay if needed (but not if photo has a thumbnail)
    const statusOverlay = PhotoStatusOverlay.createOverlayHTML(photo.processing_status, !!thumbUrl);
    if (statusOverlay) {
      imgContainer.insertAdjacentHTML('beforeend', statusOverlay);
    }
    
    // Show checkmark for selected (but not active) photos
    if (isSelected && !isActive) {
      const checkmark = document.createElement('div');
      checkmark.className = 'absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center z-10';
      checkmark.innerHTML = '<svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"></path></svg>';
      div.appendChild(checkmark);
    }
    
    // Show special indicator for active photo
    if (isActive) {
      const activeIndicator = document.createElement('div');
      activeIndicator.className = 'absolute top-2 right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center z-10 shadow-lg';
      activeIndicator.innerHTML = '<svg class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a8 8 0 100 16 8 8 0 000-16z"></path></svg>';
      div.appendChild(activeIndicator);
    }
    
    wrapper.appendChild(div);
    return wrapper;
  }
  
  /**
   * Update an existing photo element
   * @param {HTMLElement} wrapper - The wrapper element
   * @param {Object} photo - Photo data
   * @param {number} index - Index in photos array
   * @param {boolean} shouldLoadImages - Whether to set img.src immediately
   */
  updatePhotoElement(wrapper, photo, index, shouldLoadImages = true) {
    // Update position with same calculation as create
    const row = Math.floor(index / this.columnsCount);
    const col = index % this.columnsCount;
    const top = row * (this.itemHeight + this.itemGap);
    
    // Recalculate width and left in case columns changed
    const totalGapsWidth = this.itemGap * (this.columnsCount - 1);
    const itemWidth = `calc((100% - ${totalGapsWidth}px) / ${this.columnsCount})`;
    const left = `calc(${col} * ((100% - ${totalGapsWidth}px) / ${this.columnsCount} + ${this.itemGap}px))`;
    
    wrapper.style.top = `${top}px`;
    wrapper.style.left = left;
    wrapper.style.width = itemWidth;
    wrapper.dataset.index = index;
    
    // Update selection state
    const div = wrapper.querySelector('.relative');
    if (!div) {
      return;
    }
    
    const isSelected = this.selectedPhotos.has(photo.id);
    const isActive = this.activePhoto?.id === photo.id;
    
    // Update thumbnail image src if appropriate
    const imgContainer = div.querySelector('.bg-gray-800');
    if (imgContainer) {
      let img = imgContainer.querySelector('img');
      const thumbUrl = photo.thumb_256_url || photo.thumb_1024_url;
      
      if (shouldLoadImages && thumbUrl) {
        if (!img) {
          // Create img element if it doesn't exist
          img = document.createElement('img');
          img.src = thumbUrl;
          img.alt = photo.original_filename;
          img.className = 'max-w-full max-h-full object-contain';
          img.decoding = 'async';
          img.loading = 'eager';
          
          imgContainer.appendChild(img);
        } else if (img.src) {
          // If img already has a src, don't update it!
          // Presigned URLs change signatures every time, but it's the same image
          // Updating src causes browser to reload, making image disappear
        } else {
          // Img exists but has no src (shouldn't happen, but handle it)
          img.src = thumbUrl;
        }
      }
      // If !shouldLoadImages, preserve existing images but don't create new ones
    }
    
    // Update classes
    div.className = 'relative cursor-pointer rounded';
    if (isActive) {
      div.className += ' ring-4 ring-white shadow-xl';
    } else if (isSelected) {
      div.className += ' ring-4 ring-blue-400 shadow-md';
    } else {
      div.className += ' hover:ring-2 hover:ring-gray-500';
    }
    
    // Update processing status overlay (but not if photo has a thumbnail)
    const thumbUrl = photo.thumb_256_url || photo.thumb_1024_url;
    PhotoStatusOverlay.updatePhotoOverlay(div, photo.processing_status, !!thumbUrl);
    
    // Update indicators (remove old ones)
    wrapper.querySelectorAll('.absolute.top-2.right-2').forEach(el => el.remove());
    
    if (isSelected && !isActive) {
      const checkmark = document.createElement('div');
      checkmark.className = 'absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center z-10';
      checkmark.innerHTML = '<svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"></path></svg>';
      div.appendChild(checkmark);
    }
    
    if (isActive) {
      const activeIndicator = document.createElement('div');
      activeIndicator.className = 'absolute top-2 right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center z-10 shadow-lg';
      activeIndicator.innerHTML = '<svg class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a8 8 0 100 16 8 8 0 000-16z"></path></svg>';
      div.appendChild(activeIndicator);
    }
  }
  
  /**
   * Scroll to a specific photo
   */
  scrollToPhoto(photoId, behavior = 'auto') {
    const index = this.photos.findIndex(p => p.id === photoId);
    if (index === -1) return;
    
    const row = Math.floor(index / this.columnsCount);
    const top = row * (this.itemHeight + this.itemGap);
    
    if (this.scrollContainer) {
      this.scrollContainer.scrollTo({
        top: top - 100, // Offset from top
        behavior
      });
    }
  }
  
  /**
   * Get photo at a specific point (for click handling)
   */
  getPhotoAtPoint(x, y) {
    // Check rendered items first (fast path)
    for (const [photoId, element] of this.renderedItems) {
      const rect = element.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        return this.photos.find(p => p.id === photoId);
      }
    }
    return null;
  }
  
  /**
   * Clean up
   */
  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    
    this.renderedItems.clear();
    this.container.innerHTML = '';
  }
}

